import React, { JSX, useEffect, useMemo, useState } from 'react';
import {
    MessageSquare,
    Star,
    FileBarChart2,
    Clock,
    Settings,
    Zap,
    Plus,
    X,
    Minimize2,
    Maximize2,
} from 'lucide-react';
import ChatAgentPanel from './ChatAgentPanel';
import FavoritesPanel from './FavoritesPanel';
import ReportsPanel from './Reports';
import StandaloneWorkflows from './StandaloneWorkflows';
import { apiService } from '../../components/api/apiService';
import apiClient from '../../components/api/apiClient';
import { getUserInfo } from '../../routes/decodedAdmin';
import LanguageSelector from './dynamicforms/LanguageSelector';
import { RoutePrivilege } from '../portal/configurations/AccessSettings';
type ChatMessage = {
    sender: 'user' | 'agent';
    content: string | JSX.Element;
    type?: 'text' | 'report';
    usedContexts?: {
        contextid: string;
        collection_name: string;
        datasourceid: string;
        chunk_count: number;
    }[];
};
type ChatSession = {
    session_id: string;
    description: string;
    messages: {
        role: 'user' | 'assistant';
        content: string;
        timestamp: string;
        usedContexts?: {
            contextid: string;
            collection_name: string;
            datasourceid: string;
            chunk_count: number;
        }[];
    }[];
};
type Language = 'en' | 'ar';

type ChatTab = {
    id: string;
    name: string;
    sessionId: string;
};
type ContextItem = {
    id: string;
    title: string;
    collection_namespace: string;
    data_source_id: string;
};
type ChatState = {
    messages: ChatMessage[];
    input: string;
    selectedContexts: ContextItem[];
};

const ChatPage = () => {
    const [language, setLanguage] = useState<Language>(() => {
        return (localStorage.getItem('language') as Language) || 'en';
    });


    const [activeTab, setActiveTab] = useState<'chat' | 'favorites' | 'reports' | 'history' | 'settings' | 'workflows'>('chat');
    const [isMaximized, setIsMaximized] = useState(true);

    // const [tabs, setTabs] = useState<{ id: string; name: string }[]>([
    //     { id: 'tab-1', name: 'General' }
    // ]);
    // const [activeTabId, setActiveTabId] = useState('tab-1');

    const [maximizedTabs, setMaximizedTabs] = useState<{ [tabId: string]: boolean }>({
        'tab-1': true
    });

    const routeAccess: RoutePrivilege[] = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user_routes') || '[]');
        } catch {
            return [];
        }
    }, []);

    const hasAccess = (id: string, routes: RoutePrivilege[] = routeAccess): boolean => {
        for (const route of routes) {
            if (route.id === id) return route.hasAccess ?? false;
            if (Array.isArray(route.children) && route.children.length > 0) {
                const found = hasAccess(id, route.children);
                if (found) return true;
            }
        }
        return false;
    };

    //=================chat session management =========================
    useEffect(() => {
        const loadSessions = async () => {
            try {
                const sessionList: ChatSession[] = await apiService.get('/sessions/getsessions', {}, 'selfic_core');

                // ✅ No sessions → create one
                if (!sessionList || sessionList.length === 0) {
                    const defaultName = language === 'ar' ? 'عام' : 'General';
                    const response = await apiService.post(`/sessions/create?description=${encodeURIComponent(defaultName)}`, {}, 'selfic_core');
                    const newSessionId = response.session_id;

                    setChatTabs([{ id: newSessionId, name: defaultName, sessionId: newSessionId }]);
                    setActiveChatTab(newSessionId);
                    setChatStates({
                        [newSessionId]: {
                            messages: [
                                {
                                    sender: 'agent',
                                    content: language === 'ar'
                                        ? 'مرحبًا! أنا مساعد Selfic. كيف يمكنني مساعدتك اليوم؟'
                                        : 'Hi there! I am Selfic Agent. How can I assist you today?',
                                    type: 'text',
                                },
                            ],
                            input: '',
                            selectedContexts: [],
                        },
                    });
                    return;
                }

                // ✅ Sessions exist → load them
                const tabs: ChatTab[] = [];
                const stateMap: Record<string, ChatState> = {};

                for (const session of sessionList) {
                    const sessionId = session.session_id;

                    tabs.push({
                        id: sessionId,
                        name: session.description || (language === 'ar' ? 'عام' : 'General'),
                        sessionId: sessionId,
                    });

                    const mappedMessages: ChatMessage[] = session.messages?.map((m) => ({
                        sender: m.role === 'user' ? 'user' : 'agent',
                        content: m.content,
                        type: 'text',
                        usedContexts: m.usedContexts || [],
                    })) || [];

                    if (mappedMessages.length === 0) {
                        mappedMessages.push({
                            sender: 'agent',
                            content: language === 'ar'
                                ? 'مرحبًا! أنا مساعد Selfic. كيف يمكنني مساعدتك اليوم؟'
                                : 'Hi there! I am Selfic Agent. How can I assist you today?',
                            type: 'text',
                        });

                    }

                    stateMap[sessionId] = {
                        messages: mappedMessages,
                        input: '',
                        selectedContexts: [],
                    };
                }

                setChatTabs(tabs);
                setChatStates(stateMap);
                setActiveChatTab(tabs[0].id);
            } catch (err) {
                console.error('Failed to load sessions:', err);
            }
        };

        loadSessions();
    }, []);


    const createNewChatTab = async () => {
        try {
            const newName = language === 'ar'
                ? `دردشة ${chatTabs.length + 1}`
                : `Chat ${chatTabs.length + 1}`;
            const response = await apiService.post(`/sessions/create?description=${encodeURIComponent(newName)}`, {}, 'selfic_core');
            const sessionId = response.session_id;

            const newTab = { id: sessionId, name: newName, sessionId: sessionId, };
            setChatTabs((prev) => [...prev, newTab]);
            setActiveChatTab(sessionId);

            setChatStates((prev) => ({
                ...prev,
                [sessionId]: {
                    messages: [
                        {
                            sender: 'agent',
                            content: language === 'ar'
                                ? 'مرحبًا! أنا مساعد Selfic. كيف يمكنني مساعدتك اليوم؟'
                                : 'Hi there! I am Selfic Agent. How can I assist you today?',
                            type: 'text',
                        },
                    ],
                    input: '',
                    selectedContexts: [],
                },
            }));
        } catch (err) {
            console.error('Error creating new session:', err);
        }
    };

    const closeChatTab = async (id: string) => {
        const tabToClose = chatTabs.find((tab) => tab.id === id);
        if (!tabToClose) return;

        try {
            await apiService.post(`/sessions/deleteSession/${tabToClose.sessionId}`, {}, 'selfic_core');
            console.log(`Session ${tabToClose.sessionId} deleted successfully.`);
        } catch (error) {
            console.error('Failed to delete session:', error);
        }

        const updatedTabs = chatTabs.filter((tab) => tab.id !== id);
        setChatTabs(updatedTabs);
        const isClosingActive = id === activeChatTab;

        if (isClosingActive && updatedTabs.length > 0) {
            setActiveChatTab(updatedTabs[0].id);
        }
    };

    type ChatTab = { id: string; name: string, sessionId: string; };

    // const [chatTabs, setChatTabs] = useState<ChatTab[]>([
    //     { id: 'tab-1', name: 'General', sessionId: 'tab-1' }
    // ]);
    // const [activeChatTab, setActiveChatTab] = useState('tab-1');
    const [chatTabs, setChatTabs] = useState<ChatTab[]>([]);
    const [activeChatTab, setActiveChatTab] = useState<string | null>(null);

    // const [maximizedTabsChat, setMaximizedTabsChat] = useState<Record<string, boolean>>({
    //     'tab-1': true
    // });
    const [maximizedTabsChat, setMaximizedTabsChat] = useState<Record<string, boolean>>({});
    //************Session Maintinance of tabs */
    type ContextItem = {
        id: string;
        title: string;
        collection_namespace: string;
        data_source_id: string;
    };
    type ChatTabState = {
        messages: ChatMessage[];
        input: string;
        selectedContexts: ContextItem[]; // ✅ fixed
    };


    // Add this inside ChatPage component state
    const [chatStates, setChatStates] = useState<Record<string, ChatTabState>>({
        'tab-1': {
            messages: [
                {
                    sender: 'agent',
                    content: language === 'ar'
                        ? 'مرحبًا! أنا مساعد Selfic. كيف يمكنني مساعدتك اليوم؟'
                        : 'Hi there! I am Selfic Agent. How can I assist you today?',
                    type: 'text',
                },
            ],
            input: '',
            selectedContexts: [],
        },
    });

    const [editingTabId, setEditingTabId] = useState<string | null>(null);


    const handleTabRename = (id: string, newName: string) => {
        setChatTabs((prev) =>
            prev.map((tab) => (tab.id === id ? { ...tab, name: newName } : tab))
        );
    };
    const onToggleMax = () => {
        setIsMaximized((prev) => !prev);
    };

    const handleRename = async (tab: ChatTab) => {
        setEditingTabId(null);
        try {
            await apiService.post('/sessions/update-description', {
                session_id: tab.sessionId,
                description: tab.name,
            }, 'selfic_core');
        } catch (err) {
            console.error("Failed to update session description:", err);
        }
    };

    const [user, setUser] = useState<{ name: string; email: string } | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('token'); // or whatever key you use
        if (token) {
            const userInfo = getUserInfo(token);
            if (userInfo) {
                setUser({ name: userInfo.name, email: userInfo.useremail });
            }
        }
    }, []);

    const handleLanguageChange = (newLang: string) => {
        localStorage.setItem('language', newLang);
        window.location.reload(); // Reload to apply layout changes like RTL
    };
    return (
        <div className="h-screen text-white overflow-hidden" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {/* Sidebar */}
            <div className="h-full w-full">
                <div className={`flex h-full w-full ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`}>

                    <div
                        className={`w-16 bg-neutral-950 flex flex-col py-4 justify-between border-neutral-800 ${language === 'ar' ? 'border-l order-last' : 'border-r order-first'
                            }`}
                    >
                        {/* Top: Logo + Navigation */}
                        <div className="flex flex-col items-center space-y-6">
                            {/* Logo */}
                            <div className="mb-4 flex flex-col items-center gap-1 cursor-pointer">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 64 64"
                                    width="28"
                                    height="28"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="text-indigo-400"
                                >
                                    <polygon points="32,4 56,18 56,46 32,60 8,46 8,18" strokeLinejoin="round" />
                                    <circle cx="32" cy="32" r="6" />
                                    <line x1="32" y1="26" x2="32" y2="12" strokeLinecap="round" />
                                    <line x1="38" y1="32" x2="50" y2="32" strokeLinecap="round" />
                                    <line x1="32" y1="38" x2="32" y2="52" strokeLinecap="round" />
                                    <line x1="26" y1="32" x2="14" y2="32" strokeLinecap="round" />
                                    <circle cx="32" cy="12" r="1.5" fill="currentColor" />
                                    <circle cx="32" cy="52" r="1.5" fill="currentColor" />
                                    <circle cx="14" cy="32" r="1.5" fill="currentColor" />
                                    <circle cx="50" cy="32" r="1.5" fill="currentColor" />
                                </svg>
                                <span className="text-[10px] text-white font-medium tracking-tight">SelficAI</span>
                            </div>

                            {/* Navigation Buttons */}
                            {/* Each Button with Tooltip */}
                            {hasAccess('q3r4s5t6-u7v8-9012-7890-123456789012') && (

                                <div className="relative group">
                                    <button onClick={() => setActiveTab('chat')}>
                                        <MessageSquare className="text-white hover:text-indigo-400 transition" />
                                    </button>
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-12' : 'left-12'
                                            } bg-neutral-800 text-white text-xs px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap`}
                                    >

                                        Chat
                                    </div>
                                </div>
                            )}

                            {hasAccess('u7v8w9x0-y1z2-3456-1234-567890123456') && (
                                <div className="relative group">
                                    <button onClick={() => setActiveTab('favorites')}>
                                        <Star className="text-white hover:text-indigo-400 transition" />
                                    </button>
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-12' : 'left-12'
                                            } bg-neutral-800 text-white text-xs px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap`}
                                    >

                                        Favorites
                                    </div>
                                </div>
                            )}

                            {hasAccess('s5t6u7v8-w9x0-1234-9012-345678901234') && (
                                <div className="relative group">
                                    <button onClick={() => setActiveTab('workflows')}>
                                        <Zap className="text-white hover:text-indigo-400 transition" />
                                    </button>
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-12' : 'left-12'
                                            } bg-neutral-800 text-white text-xs px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap`}
                                    >

                                        Workflows
                                    </div>
                                </div>
                            )}

                            {hasAccess('r4s5t6u7-v8w9-0123-8901-234567890123') && (
                                <div className="relative group">
                                    <button onClick={() => setActiveTab('reports')}>
                                        <FileBarChart2 className="text-white hover:text-indigo-400 transition" />
                                    </button>
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-12' : 'left-12'
                                            } bg-neutral-800 text-white text-xs px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap`}
                                    >

                                        Reports
                                    </div>
                                </div>
                            )}

                            {hasAccess('v8w9x0y1-z2a3-4567-2345-678901234567') && (

                                <div className="relative group">
                                    <button onClick={() => setActiveTab('history')}>
                                        <Clock className="text-white hover:text-indigo-400 transition" />
                                    </button>
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-12' : 'left-12'
                                            } bg-neutral-800 text-white text-xs px-3 py-1.5 rounded shadow-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap`}
                                    >

                                        History
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom: Language + User + Logout */}
                        <div className="flex flex-col items-center space-y-4 mb-2">

                            {/* Language Selector */}
                            <LanguageSelector currentLanguage={language} onChange={handleLanguageChange} />

                            {/* Avatar with Tooltip */}
                            <div className="group relative">
                                <img
                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name ?? 'Guest')}&background=ffffff&color=1e293b`}
                                    alt="User Avatar"
                                    className="w-9 h-9 rounded-full border border-neutral-700 shadow cursor-pointer"
                                />
                                <div className="absolute left-12 top-1/2 transform -translate-y-1/2 w-max px-3 py-1.5 text-xs text-white bg-neutral-800 rounded shadow-lg opacity-0 group-hover:opacity-100 transition">
                                    <div className="font-semibold">{user?.name ?? 'Guest'}</div>
                                    <div className="text-gray-400">{user?.email ?? 'user@unknown.com'}</div>
                                </div>
                            </div>

                            {/* Logout */}
                            <button
                                onClick={() => {
                                    localStorage.clear();
                                    window.location.href = '/login';
                                }}
                                title="Logout"
                                className="text-gray-400 hover:text-red-500 transition"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="h-5 w-5"
                                >
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                    <polyline points="10 17 15 12 10 7" />
                                    <line x1="15" y1="12" x2="3" y2="12" />
                                </svg>
                            </button>
                        </div>

                    </div>

                    {/* Main Content */}
                    <div className="flex-1 flex justify-center items-stretch">
                        <div className={`${isMaximized ? 'w-full' : 'w-[80%]'} h-full`}>
                            {activeTab === 'chat' && hasAccess('q3r4s5t6-u7v8-9012-7890-123456789012') && (
                                <div className="flex h-screen">
                                    <div className="flex flex-col h-full w-full bg-white overflow-hidden">

                                        {/* Tab Header */}
                                        <div className="w-full flex justify-center bg-white border-b border-gray-200">
                                            <div className="flex items-center gap-2 px-4 py-2 rounded-t-lg max-w-4xl w-full justify-center">

                                                {chatTabs.map((tab) => (
                                                    <div
                                                        key={tab.id}
                                                        onClick={() => setActiveChatTab(tab.id)}
                                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-full cursor-pointer transition-all text-sm
              ${tab.id === activeChatTab
                                                                ? 'bg-indigo-100 text-indigo-700 font-semibold shadow border border-indigo-300'
                                                                : 'bg-gray-100 text-gray-600 hover:text-black hover:bg-gray-200 border border-transparent'}
            `}
                                                    >
                                                        <MessageSquare className="w-4 h-4 text-indigo-500 shrink-0" />

                                                        {editingTabId === tab.id ? (
                                                            <input
                                                                autoFocus
                                                                className="bg-transparent border border-gray-300 text-xs px-2 py-0.5 rounded text-gray-800 w-24"
                                                                value={tab.name}
                                                                onChange={(e) => handleTabRename(tab.id, e.target.value)}
                                                                onBlur={() => handleRename(tab)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        handleRename(tab);
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <span
                                                                className="text-xs truncate max-w-[100px]"
                                                                onDoubleClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingTabId(tab.id);
                                                                }}
                                                                title="Double-click to rename"
                                                            >
                                                                {tab.name}
                                                            </span>
                                                        )}

                                                        <button
                                                            className="ml-1 opacity-60 hover:opacity-100 text-gray-400 hover:text-red-500 transition"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                closeChatTab(tab.id);
                                                            }}
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ))}

                                                {/* Add Tab Button */}
                                                <button
                                                    onClick={createNewChatTab}
                                                    title="New tab"
                                                    className="ml-2 flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-indigo-100 hover:text-indigo-700 transition"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Active ChatAgentPanel */}
                                        <div className="flex flex-col flex-1 h-full w-full bg-white min-h-0">
                                            {activeChatTab && chatTabs.map((tab) =>
                                                tab.id === activeChatTab ? (
                                                    <ChatAgentPanel
                                                        key={tab.id}
                                                        tabId={tab.id}
                                                        sessionId={tab.sessionId}
                                                        messages={chatStates[tab.id]?.messages || []}
                                                        input={chatStates[tab.id]?.input || ''}
                                                        language={language}
                                                        selectedContexts={chatStates[tab.id]?.selectedContexts || []}
                                                        onStateChange={(newState) => {
                                                            setChatStates((prev) => ({
                                                                ...prev,
                                                                [tab.id]: {
                                                                    ...prev[tab.id],
                                                                    ...newState,
                                                                },
                                                            }));
                                                        }}
                                                    />
                                                ) : null
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'favorites' && hasAccess('u7v8w9x0-y1z2-3456-1234-567890123456') && (
                                <div className="flex h-screen">
                                    <div className="flex-1 overflow-y-auto bg-black">
                                        <FavoritesPanel />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'reports' && hasAccess('r4s5t6u7-v8w9-0123-8901-234567890123') && (
                                <div className="flex h-screen">
                                    <div className="flex-1 overflow-y-auto bg-black">
                                        <ReportsPanel />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'workflows' && hasAccess('s5t6u7v8-w9x0-1234-9012-345678901234') && (
                                <div className="flex h-screen">
                                    <div className="flex-1 overflow-y-auto bg-black">
                                        <StandaloneWorkflows />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'history' && hasAccess('v8w9x0y1-z2a3-4567-2345-678901234567') && (
                                <div className="p-6 text-gray-400">🕓 Review previous conversations.</div>
                            )}
                            {activeTab === 'settings' && (
                                <div className="p-6 text-gray-400">⚙️ Adjust agent settings like model, temperature, etc.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatPage;
